import { useState } from "react";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import useTemplateEditor from "../../data/useTemplateEditor";
import { IConditionDescriptionPreview } from "../../models/TemplateEditor";
import { CONDITION_DESCRIPTION_MAX_LENGTH } from "../../data/useTemplateConditionDescription";

interface ConditionDescriptionPreviewProps {
  body: string;
}

export default function ConditionDescriptionPreview({ body }: ConditionDescriptionPreviewProps) {
  const { previewConditionDescription } = useTemplateEditor();

  const [tagNumber, setTagNumber] = useState("");
  const [preview, setPreview] = useState<IConditionDescriptionPreview | null>(null);
  const [previewedBody, setPreviewedBody] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isOutOfDate = preview !== null && previewedBody !== body;

  const handlePreview = async () => {
    setError(null);

    try {
      const response = await previewConditionDescription.mutateAsync({
        body,
        tagNumber: tagNumber.trim() || null,
      });
      setPreview(response.data);
      setPreviewedBody(body);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to generate preview");
    }
  };

  return (
    <div className="rounded-md border border-gray-200">
      <div className="flex flex-col sm:flex-row gap-3 sm:items-end justify-between p-4 border-b border-gray-200 bg-gray-50">
        <div>
          <p className="text-sm font-medium text-gray-900">Preview</p>
          <p className="text-sm text-gray-500">
            Shows the text that goes in a listing's condition box. Leave the tag number blank to use a sample part.
          </p>
        </div>
        <div className="flex gap-3 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tag Number</label>
            <input
              type="text"
              value={tagNumber}
              onChange={(e) => setTagNumber(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handlePreview();
              }}
              placeholder="Sample part"
              className="w-40 rounded-md border border-gray-300 text-sm"
            />
          </div>
          <ButtonInput
            label={preview ? "Refresh Preview" : "Preview"}
            onClick={handlePreview}
            isLoading={previewConditionDescription.isLoading}
            classes="py-2.5 whitespace-nowrap"
          />
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border-b border-red-200 text-red-700 px-4 py-3 text-sm">
          {error}
        </div>
      )}

      {preview ? (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2 text-sm text-gray-600 border-b border-gray-200">
            <span>
              Showing <span className="font-medium text-gray-900">{preview.tagNumber}</span>
              {preview.title && <> &middot; {preview.title}</>}
            </span>
            <span className="flex flex-wrap items-center gap-3">
              {isOutOfDate && (
                <span className="text-amber-700">Template changed since this preview &mdash; refresh to update</span>
              )}
              <span className={preview.truncated ? "font-semibold text-red-600" : "text-gray-500"}>
                {preview.length.toLocaleString()} / {CONDITION_DESCRIPTION_MAX_LENGTH.toLocaleString()} characters
              </span>
            </span>
          </div>
          {preview.truncated && (
            <div className="bg-amber-50 border-b border-amber-200 text-amber-800 px-4 py-2 text-sm">
              This part's text was over eBay's {CONDITION_DESCRIPTION_MAX_LENGTH.toLocaleString()}-character limit, so it has been cut at the last full sentence that fits.
            </div>
          )}
          <p className="px-4 py-4 text-sm text-gray-900 whitespace-pre-wrap break-words">
            {preview.text || <span className="text-gray-400">(empty)</span>}
          </p>
        </>
      ) : (
        <p className="px-4 py-6 text-sm text-gray-500">
          Click Preview to see the condition description for a part.
        </p>
      )}
    </div>
  );
}
